import { cn } from '@commenda/utils';
import React, { createContext, forwardRef, useCallback, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from '../IconButton';
import { Icons } from '../Icons';

/**
 * Context used by compound subcomponents (e.g. FullscreenModalActions,
 * FullscreenModalClose) to access the `onClose` handler supplied to the root.
 */
interface IFullscreenModalContextValue {
  onClose: () => void;
}

const FullscreenModalContext = createContext<IFullscreenModalContextValue | null>(null);

function useFullscreenModalContext(caller: string): IFullscreenModalContextValue {
  const ctx = useContext(FullscreenModalContext);
  if (!ctx) {
    throw new Error(`${caller} must be rendered inside <FullscreenModal>`);
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export interface FullscreenModalProps {
  /** Whether the modal is open. When false, nothing is rendered. */
  isOpen: boolean;
  /** Called when the modal requests close (close button, Escape key). */
  onClose: () => void;
  /**
   * Whether pressing Escape should call `onClose`. Defaults to `false` —
   * callers must opt in.
   */
  enableEscapeKey?: boolean;
  /** Extra classes applied to the fullscreen container. */
  className?: string;
  children: React.ReactNode;
}

/**
 * A base full-screen modal primitive. Provides:
 * - Portal rendering to `document.body` at `z-40` so kakashi `Dialog` (z-50)
 *   and other overlay primitives stack above it correctly.
 * - Fullscreen container with semantic `role="dialog" aria-modal="true"`.
 * - Compound subcomponents for composing a top bar (Header/Title/Actions)
 *   and a main body (Body/Content/Sidebar).
 * - Opt-in Escape-to-close via `enableEscapeKey`.
 *
 * Contains zero document-specific content — consumers fill in every slot.
 *
 * @example
 * ```tsx
 * <FullscreenModal isOpen={isOpen} onClose={onClose} enableEscapeKey>
 *   <FullscreenModalHeader>
 *     <FullscreenModalTitle>My document</FullscreenModalTitle>
 *     <FullscreenModalActions>
 *       <Button size="sm">Download</Button>
 *     </FullscreenModalActions>
 *   </FullscreenModalHeader>
 *   <FullscreenModalBody>
 *     <FullscreenModalContent>main</FullscreenModalContent>
 *     <FullscreenModalSidebar>rail</FullscreenModalSidebar>
 *   </FullscreenModalBody>
 * </FullscreenModal>
 * ```
 */
export function FullscreenModal({ isOpen, onClose, enableEscapeKey = false, className, children }: FullscreenModalProps) {
  useEffect(() => {
    if (!isOpen || !enableEscapeKey) return undefined;
    const handler = (e: KeyboardEvent) => {
      // Escape belongs to the topmost layer. A nested overlay marks the event handled when it
      // closes on it — Radix calls preventDefault on the layer it dismisses, from a document
      // listener that runs before this one — so without the check this modal closes behind
      // whatever the user was actually dismissing, taking their unsaved work with it.
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, enableEscapeKey, onClose]);

  const contextValue = React.useMemo(() => ({ onClose }), [onClose]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <FullscreenModalContext.Provider value={contextValue}>
      <div
        role="dialog"
        aria-modal="true"
        className={cn('fixed inset-0 z-40 flex flex-col bg-bg-primary', className)}
      >
        {children}
      </div>
    </FullscreenModalContext.Provider>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export interface FullscreenModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/** Top-bar row of the fullscreen modal. Compose `FullscreenModalTitle` and `FullscreenModalActions` inside. */
export const FullscreenModalHeader = forwardRef<HTMLDivElement, FullscreenModalHeaderProps>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center justify-between gap-4 border-b border-border-secondary px-4 py-2.5', className)}
    {...props}
  >
    {children}
  </div>
));
FullscreenModalHeader.displayName = 'FullscreenModalHeader';

// ---------------------------------------------------------------------------
// Title
// ---------------------------------------------------------------------------

export interface FullscreenModalTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

/** Semantic title for the modal. Renders an `<h2>`. */
export const FullscreenModalTitle = forwardRef<HTMLHeadingElement, FullscreenModalTitleProps>(({ className, children, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn('flex min-w-0 items-center gap-2 text-sm font-medium text-text-primary truncate', className)}
    {...props}
  >
    {children}
  </h2>
));
FullscreenModalTitle.displayName = 'FullscreenModalTitle';

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export interface FullscreenModalActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  /** When true, the auto-appended close icon is not rendered. */
  hideCloseIcon?: boolean;
}

/**
 * Right-side container for action buttons in the Header. By default, auto-appends
 * a Close icon wired to the root's `onClose`. Opt out with `hideCloseIcon`.
 */
export const FullscreenModalActions = forwardRef<HTMLDivElement, FullscreenModalActionsProps>(
  ({ className, children, hideCloseIcon = false, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-2', className)} {...props}>
      {children}
      {!hideCloseIcon && <FullscreenModalClose />}
    </div>
  ),
);
FullscreenModalActions.displayName = 'FullscreenModalActions';

// ---------------------------------------------------------------------------
// Close button (exposed as a compound piece for consumers who need precise placement)
// ---------------------------------------------------------------------------

export interface FullscreenModalCloseProps {
  className?: string;
}

/** IconButton that calls the modal's `onClose`. Normally auto-appended by `FullscreenModalActions`. */
export const FullscreenModalClose = forwardRef<HTMLButtonElement, FullscreenModalCloseProps>(({ className }, ref) => {
  const { onClose } = useFullscreenModalContext('FullscreenModalClose');
  const handleClick = useCallback(() => onClose(), [onClose]);
  return (
    <IconButton
      ref={ref}
      size="sm"
      variant="default"
      icon={<Icons.XClose className="size-5" />}
      aria-label="Close"
      onClick={handleClick}
      className={className}
    />
  );
});
FullscreenModalClose.displayName = 'FullscreenModalClose';

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

export interface FullscreenModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/** Flex row wrapper for Content + optional Sidebar. Takes remaining vertical space. */
export const FullscreenModalBody = forwardRef<HTMLDivElement, FullscreenModalBodyProps>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-1 min-h-0', className)} {...props}>
    {children}
  </div>
));
FullscreenModalBody.displayName = 'FullscreenModalBody';

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

export interface FullscreenModalContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/** Main content area. Takes remaining horizontal space next to an optional Sidebar. */
export const FullscreenModalContent = forwardRef<HTMLDivElement, FullscreenModalContentProps>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('relative flex-1 min-w-0', className)} {...props}>
    {children}
  </div>
));
FullscreenModalContent.displayName = 'FullscreenModalContent';

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

export interface FullscreenModalSidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/** Optional right rail. Consumer only renders this when a sidebar is needed. */
export const FullscreenModalSidebar = forwardRef<HTMLDivElement, FullscreenModalSidebarProps>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('w-[280px] shrink-0 overflow-y-auto border-l border-border-secondary', className)} {...props}>
    {children}
  </div>
));
FullscreenModalSidebar.displayName = 'FullscreenModalSidebar';
